import { useAuth } from '@amzn/xb-hooks';
import { RoleType, AuthEndpoint, APPLICATION_URL } from '../../common';
import { useGetCurrentUser } from './getCurrentUser';
import { getTimezoneOrDefault } from '../../common/dateUtils';
import { useEffect, useRef, useState } from 'react';
import { getNodeEnvironment } from '../../common/nodeEnvironment';

export interface ApplicationUser {
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: RoleType;
    groups?: string[];
    timezone?: string;
}

const addParamsAndCreateFullURL = (endpoint: AuthEndpoint, searchParams: Record<string, string> = {}) => {
    const nodeEnvironment = getNodeEnvironment();
    let currentDomain;
    switch (nodeEnvironment) {
        case 'beta':
            currentDomain = APPLICATION_URL.BETA;
            break;
        case 'gamma':
            currentDomain = APPLICATION_URL.GAMMA;
            break;
        case 'prod':
            currentDomain = APPLICATION_URL.PROD;
            break;
        default:
            currentDomain = 'http://localhost:3000';
            break;
    }
    const urlWithPath = new URL(`${currentDomain}/${endpoint}`);
    Object.entries(searchParams).forEach(([key, value]) => {
        urlWithPath.searchParams.set(key, value);
    });
    urlWithPath.searchParams.set('redirect_uri', searchParams.redirect_uri ?? currentDomain);

    return urlWithPath.toString();
};

const refreshToken = async (): Promise<Response> => {
    const refreshTokenURL = addParamsAndCreateFullURL(AuthEndpoint.REFRESH_TOKEN);
    return fetch(refreshTokenURL, { method: 'POST' });
};

const isAuthorizationError = (error: any): boolean => {
    return (
        error?.message?.toLowerCase().includes('not authorized') ||
        error?.message?.toLowerCase().includes('unauthorized') ||
        error?.graphQLErrors?.some(
            (gqlError: any) =>
                gqlError?.extensions?.code === 'NOT_AUTHORIZED' ||
                gqlError?.message?.toLowerCase().includes('not authorized') ||
                gqlError?.message?.toLowerCase().includes('unauthorized'),
        )
    );
};

export const useUserInfo = () => {
    const [user, setUser] = useState<ApplicationUser>({});
    const [fetchInProgress, setFetchInProgress] = useState<boolean>(true);

    // Track if we've already initiated the auth flow to prevent loops
    const authFlowInitiatedRef = useRef(false);

    const { data, error, loading, refetch } = useGetCurrentUser();
    const { login } = useAuth();

    const setupUserData = (userData: any) => {
        setFetchInProgress(false);
        setUser({
            id: userData.currentUser.id,
            firstName: userData.currentUser.firstName,
            lastName: userData.currentUser.lastName,
            email: userData.currentUser.emailAddress,
            role: userData.currentUser.userRole,
            groups: userData.currentUser.groups?.nodes.map((group: { name: string }) => group.name),
            timezone: getTimezoneOrDefault(userData.currentUser.preferences?.timezone),
        });
    };

    useEffect(() => {
        // Only process when we have data or error, not during loading
        if (loading) {
            return;
        }

        // Skip if we've already initiated the auth flow
        if (authFlowInitiatedRef.current) {
            return;
        }

        // Mark that we've initiated the auth flow
        authFlowInitiatedRef.current = true;

        // Handle error from getCurrentUser
        if (error) {
            // If it's an authorization error, try to refresh token first
            if (isAuthorizationError(error)) {
                refreshToken()
                    .then((response) => {
                        if (response.ok) {
                            // Token refreshed successfully, retry getCurrentUser
                            refetch().then((result) => {
                                if (result.data) {
                                    setupUserData(result.data);
                                } else {
                                    // Still failed after refresh, redirect to login
                                    login(window.location.href);
                                }
                            });
                        } else {
                            // Refresh token failed, redirect to login
                            login(window.location.href);
                        }
                    })
                    .catch(() => {
                        // Refresh token failed, redirect to login
                        login(window.location.href);
                    });
            } else {
                // Non-authorization error, redirect to login
                login(window.location.href);
            }
            return;
        }

        // Handle success from getCurrentUser - just set up the user data
        if (data) {
            setupUserData(data);
        }
    }, [data, error, loading, refetch, login]);

    return { user, loading: fetchInProgress };
};
